import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import {
  FiSun,
  FiMoon,
  FiMenu,
  FiX,
  FiExternalLink,
  FiSearch,
  FiArrowUpRight,
} from "react-icons/fi";

const navItems: { label: string; to: string; external?: boolean }[] = [
  { label: "Home", to: "/" },
  { label: "Brain", to: "https://brain.rakeshkadam.me", external: true },
  { label: "Skills", to: "/skills" },
  { label: "Blog", to: "/blogs" },
  { label: "Certifications", to: "/certificates" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const searchItems = [
  { label: "Home", to: "/", type: "page" as const },
  { label: "Skills", to: "/skills", type: "page" as const },
  { label: "Blog", to: "/blogs", type: "page" as const },
  { label: "Certifications", to: "/certificates", type: "page" as const },
  { label: "About", to: "/about", type: "page" as const },
  { label: "Contact", to: "/contact", type: "page" as const },
  { label: "Digital Brain", to: "https://brain.rakeshkadam.me", type: "external" as const },
  { label: "GitHub", to: "https://github.com/rakesh-kadam", type: "external" as const },
  { label: "LinkedIn", to: "https://linkedin.com/in/rakeshkadam", type: "external" as const },
];

const Navbar = () => {
  const { isDark, toggle } = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setMobileOpen(false), 0);
    return () => clearTimeout(timer);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        setSearchOpen(false);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (searchOpen) {
      inputRef.current?.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    if (searchOpen) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [searchOpen]);

  const isActive = (to: string) =>
    to === "/" ? pathname === "/" : pathname.startsWith(to);

  const results = useMemo(() => {
    if (!query.trim()) return searchItems.slice(0, 6);
    const q = query.toLowerCase();
    return searchItems.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.to.toLowerCase().includes(q)
    );
  }, [query]);

  const handleResultClick = (item: (typeof searchItems)[number]) => {
    setSearchOpen(false);
    setQuery("");
    if (item.type === "external") {
      window.open(item.to, "_blank", "noopener noreferrer");
    } else {
      navigate(item.to);
    }
  };

  return (
    <>
      <header
        className={`sticky top-0 z-50 border-b transition-all duration-300 ${
          scrolled
            ? "py-2.5 border-light-border-strong/60 dark:border-border-subtle bg-light-surface/80 dark:bg-surface-0/80 backdrop-blur-xl"
            : "py-4 border-transparent bg-light-bg/60 dark:bg-surface-0/60 backdrop-blur-md"
        }`}
      >
        <nav className="mx-auto flex max-w-6xl items-center justify-between px-6 md:px-10">
          <Link
            to="/"
            className="text-lg font-bold tracking-tight text-light-text dark:text-text-primary hover:text-accent dark:hover:text-accent transition-colors"
          >
            Rakesh Kadam
          </Link>

          {/* Desktop */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) =>
              item.external ? (
                <a
                  key={item.to}
                  href={item.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-1 px-3 py-2 text-sm font-medium text-light-muted hover:text-light-text dark:text-text-muted dark:hover:text-text-primary transition-colors rounded-md hover:bg-light-surface-2 dark:hover:bg-surface-1"
                >
                  {item.label}
                  <FiExternalLink
                    size={12}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-text-faint"
                  />
                </a>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`relative px-3 py-2 text-sm font-medium rounded-md transition-all ${
                    isActive(item.to)
                      ? "text-light-text dark:text-text-primary bg-light-surface-2 dark:bg-surface-1"
                      : "text-light-muted hover:text-light-text dark:text-text-muted dark:hover:text-text-primary hover:bg-light-surface-2 dark:hover:bg-surface-1"
                  }`}
                >
                  {item.label}
                </Link>
              )
            )}

            <div className="ml-2 h-4 w-px bg-light-border-strong dark:bg-border-subtle" />

            <button
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              className="ml-2 rounded-md p-2 text-light-muted transition-all hover:bg-light-surface-2 hover:text-light-text dark:text-text-muted dark:hover:bg-surface-1 dark:hover:text-text-primary"
            >
              <FiSearch size={16} />
            </button>

            <button
              onClick={toggle}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              className="ml-1 rounded-md p-2 text-light-muted transition-all hover:bg-light-surface-2 hover:text-light-text dark:text-text-muted dark:hover:bg-surface-1 dark:hover:text-text-primary"
            >
              {isDark ? <FiSun size={16} /> : <FiMoon size={16} />}
            </button>
          </div>

          {/* Mobile controls */}
          <div className="flex items-center gap-1 md:hidden">
            <button
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              className="rounded-md p-2.5 text-light-muted dark:text-text-muted hover:bg-light-surface-2 dark:hover:bg-surface-1"
            >
              <FiSearch size={18} />
            </button>
            <button
              onClick={toggle}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              className="rounded-md p-2.5 text-light-muted dark:text-text-muted hover:bg-light-surface-2 dark:hover:bg-surface-1"
            >
              {isDark ? <FiSun size={18} /> : <FiMoon size={18} />}
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              className="rounded-md p-2.5 text-light-muted dark:text-text-muted hover:bg-light-surface-2 dark:hover:bg-surface-1"
            >
              {mobileOpen ? <FiX size={20} /> : <FiMenu size={20} />}
            </button>
          </div>
        </nav>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="animate-slide-up border-t border-light-border dark:border-border-subtle bg-light-surface dark:bg-surface-1 md:hidden">
            <div className="flex flex-col gap-0.5 px-4 py-3">
              {navItems.map((item) =>
                item.external ? (
                  <a
                    key={item.to}
                    href={item.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium text-light-muted dark:text-text-muted transition-colors hover:bg-light-surface-2 dark:hover:bg-surface-2"
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                    <FiExternalLink size={12} />
                  </a>
                ) : (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className={`rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive(item.to)
                        ? "text-light-text dark:text-text-primary bg-light-surface-2 dark:bg-surface-2"
                        : "text-light-muted dark:text-text-muted hover:bg-light-surface-2 dark:hover:bg-surface-2"
                    }`}
                  >
                    {item.label}
                  </Link>
                )
              )}
            </div>
          </div>
        )}
      </header>

      {/* Search modal */}
      {searchOpen && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center pt-24 px-4 bg-surface-0/50 backdrop-blur-sm">
          <div
            ref={searchRef}
            className="w-full max-w-lg rounded-xl border border-light-border bg-light-surface shadow-2xl dark:border-border-subtle dark:bg-surface-1 overflow-hidden"
          >
            <div className="flex items-center gap-3 border-b border-light-border px-4 py-3 dark:border-border-subtle">
              <FiSearch size={18} className="text-light-muted dark:text-text-faint shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search pages, links..."
                className="flex-1 bg-transparent text-sm text-light-text dark:text-text-primary placeholder:text-light-muted dark:placeholder:text-text-faint focus:outline-none"
              />
              <span className="rounded border border-light-border px-1.5 py-0.5 text-[10px] font-mono text-light-muted dark:border-border-subtle dark:text-text-faint">
                ESC
              </span>
            </div>
            <div className="max-h-[60vh] overflow-y-auto py-2">
              {results.length === 0 && (
                <p className="px-4 py-6 text-center text-sm text-light-muted dark:text-text-muted">
                  No results for &quot;{query}&quot;
                </p>
              )}
              {results.map((item) => (
                <button
                  key={item.to}
                  onClick={() => handleResultClick(item)}
                  className="flex w-full items-center justify-between px-4 py-2.5 text-sm text-light-text hover:bg-light-surface-2 dark:text-text-primary dark:hover:bg-surface-2 transition-colors"
                >
                  <span className="font-medium">{item.label}</span>
                  {item.type === "external" ? (
                    <FiExternalLink
                      size={12}
                      className="text-light-muted dark:text-text-faint"
                    />
                  ) : (
                    <FiArrowUpRight
                      size={12}
                      className="text-light-muted dark:text-text-faint"
                    />
                  )}
                </button>
              ))}
            </div>
            <div className="border-t border-light-border px-4 py-2 text-[10px] text-light-muted dark:border-border-subtle dark:text-text-faint flex items-center gap-3">
              <span>
                <kbd className="rounded border border-light-border px-1 font-mono dark:border-border-subtle">Ctrl</kbd>{" "}
                <kbd className="rounded border border-light-border px-1 font-mono dark:border-border-subtle">K</kbd> to toggle
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
